import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Play, RotateCcw, Pause } from 'lucide-react'

export interface Booking {
  day: number
  guest: string
  nights: number
  revenue: number
}

export const TOTAL_ROOMS = 6

export const BOOKINGS: Booking[] = [
  { day: -28, guest: 'Smith', nights: 2, revenue: 160 },
  { day: -21, guest: 'Jones', nights: 3, revenue: 270 },
  { day: -14, guest: 'Brown', nights: 1, revenue: 95 },
  { day: -10, guest: 'Wilson', nights: 2, revenue: 190 },
  { day: -5, guest: 'Taylor', nights: 1, revenue: 110 },
  { day: -2, guest: 'Davis', nights: 1, revenue: 125 },
]

function dayToPosition(day: number): number {
  // Map day -30 to 0% and day 0 to 100%
  return ((day + 30) / 30) * 100
}

/**
 * Pickup over the trailing 7-day window (day −7 to check-in), as an occupancy-point delta.
 *
 * This is genuine pickup per docs/revenue-vocabulary.md §6, not a count of booking records: the
 * change in on-the-books occupancy for a FIXED forward stay, read at two observation points —
 * as it stood 7 days before check-in versus now. Only the observation point moves. Because it is
 * a subtraction of two OTB observations, it goes NEGATIVE when occupancy falls in the window
 * (net cancellations), exactly as §6 requires. The live occupancy calendar words the same movement
 * in rooms ("+2 rooms" / rose when negative); this teaching tile shows it as an occupancy-point
 * delta, labelled "pts" (the one points spelling) — the unit note in the surrounding guide prose.
 */
export function pickupOccupancyPoints(visibleBookings: Booking[], totalRooms: number = TOTAL_ROOMS): number {
  const roomNightsNow = visibleBookings.reduce((sum, b) => sum + b.nights, 0)
  const roomNightsAsOf7dAgo = visibleBookings
    .filter((b) => b.day < -7)
    .reduce((sum, b) => sum + b.nights, 0)
  const occupancyNow = Math.round((roomNightsNow / totalRooms) * 100)
  const occupancyAsOf7dAgo = Math.round((roomNightsAsOf7dAgo / totalRooms) * 100)
  return occupancyNow - occupancyAsOf7dAgo
}

export default function AdrPickupExplainer() {
  const [visibleCount, setVisibleCount] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [hasStarted, setHasStarted] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const scheduleNext = useCallback(
    (currentCount: number) => {
      if (currentCount >= BOOKINGS.length) {
        setIsPlaying(false)
        return
      }
      timerRef.current = setTimeout(() => {
        setVisibleCount(currentCount + 1)
        scheduleNext(currentCount + 1)
      }, 1300)
    },
    []
  )

  const handlePlay = useCallback(() => {
    if (visibleCount >= BOOKINGS.length) {
      // Reset and replay
      setVisibleCount(0)
      setHasStarted(true)
      setIsPlaying(true)
      scheduleNext(0)
    } else {
      setHasStarted(true)
      setIsPlaying(true)
      scheduleNext(visibleCount)
    }
  }, [visibleCount, scheduleNext])

  const handlePause = useCallback(() => {
    setIsPlaying(false)
    clearTimer()
  }, [clearTimer])

  const handleReset = useCallback(() => {
    setIsPlaying(false)
    setHasStarted(false)
    setVisibleCount(0)
    clearTimer()
  }, [clearTimer])

  useEffect(() => {
    return clearTimer
  }, [clearTimer])

  // Compute running stats
  const visibleBookings = BOOKINGS.slice(0, visibleCount)
  const totalRooms = visibleBookings.reduce((sum, b) => sum + b.nights, 0)
  const totalRevenue = visibleBookings.reduce((sum, b) => sum + b.revenue, 0)
  const adr = totalRooms > 0 ? totalRevenue / totalRooms : 0
  const occupancy = Math.round((totalRooms / TOTAL_ROOMS) * 100)

  // Pickup 7d: the occupancy-point change over the trailing 7-day window (day -7 to check-in) —
  // occupancy now minus occupancy as it stood 7 days ago. A genuine on-the-books delta, not a
  // booking count, so it moves with real pickup and can go negative on cancellations.
  const pickupPts = pickupOccupancyPoints(visibleBookings)

  const isComplete = visibleCount >= BOOKINGS.length

  return (
    <div className="space-y-5">
      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border border-border/60 bg-white p-3 text-center shadow-sm">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Rooms Booked</p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-foreground tracking-tight">
            {totalRooms}
            <span className="text-sm font-normal text-muted-foreground">/{TOTAL_ROOMS}</span>
          </p>
          <p className="text-[11px] tabular-nums text-muted-foreground">{occupancy}% occupancy</p>
        </div>

        <div className="rounded-lg border border-border/60 bg-white p-3 text-center shadow-sm">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">ADR</p>
          <p className={cn(
            'mt-1 text-2xl font-bold tabular-nums tracking-tight transition-colors duration-300',
            adr > 0 ? 'text-foreground' : 'text-slate-300'
          )}>
            {adr > 0 ? `£${adr.toFixed(2)}` : '--'}
          </p>
          <p className="text-[11px] text-muted-foreground">avg daily rate</p>
        </div>

        <div className="rounded-lg border border-border/60 bg-white p-3 text-center shadow-sm">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Pickup 7d</p>
          <p className={cn(
            'mt-1 text-2xl font-bold tabular-nums tracking-tight transition-colors duration-300',
            pickupPts > 0 ? 'text-emerald-600' : pickupPts < 0 ? 'text-rose-600' : 'text-slate-300'
          )}>
            {pickupPts > 0 ? '+' : ''}{pickupPts}
            <span className="text-sm font-normal text-muted-foreground"> pts</span>
          </p>
          <p className="text-[11px] text-muted-foreground">occupancy vs 7 days ago</p>
        </div>
      </div>

      {/* Timeline */}
      <div className="relative pt-6 pb-2">
        {/* Day labels */}
        <div className="flex justify-between mb-2 px-1">
          <span className="text-[10px] text-muted-foreground tabular-nums">Day -30</span>
          <span className="text-[10px] text-muted-foreground tabular-nums">Day -20</span>
          <span className="text-[10px] text-muted-foreground tabular-nums">Day -10</span>
          <span className="text-[10px] font-semibold text-foreground tabular-nums">Check-in</span>
        </div>

        {/* Timeline bar */}
        <div className="relative h-12 mx-1">
          {/* Background track */}
          <div className="absolute top-5 left-0 right-0 h-1 bg-slate-200 rounded-full" />

          {/* 7-day pickup zone */}
          <div
            className="absolute top-4 h-3 bg-emerald-50 border border-emerald-200/60 rounded-full"
            style={{ left: `${dayToPosition(-7)}%`, right: '0%' }}
          >
            <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] font-medium text-emerald-600 whitespace-nowrap">
              7-day window
            </span>
          </div>

          {/* Booking dots */}
          {BOOKINGS.map((booking, idx) => {
            const isVisible = idx < visibleCount
            const pos = dayToPosition(booking.day)

            return (
              <div
                key={booking.guest}
                className="absolute top-3 -translate-x-1/2"
                style={{ left: `${pos}%` }}
              >
                <div
                  className={cn(
                    'relative transition-all duration-500',
                    isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                  )}
                >
                  {/* Dot */}
                  <div
                    className={cn(
                      'h-5 w-5 rounded-full border-2 border-white shadow-md flex items-center justify-center',
                      booking.day >= -7 ? 'bg-emerald-500' : 'bg-primary'
                    )}
                  >
                    <span className="text-[8px] font-bold text-white">{idx + 1}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Booking list */}
      <div className="space-y-1.5">
        {BOOKINGS.map((booking, idx) => {
          const isVisible = idx < visibleCount
          const runningRooms = BOOKINGS.slice(0, idx + 1).reduce((s, b) => s + b.nights, 0)
          const runningRev = BOOKINGS.slice(0, idx + 1).reduce((s, b) => s + b.revenue, 0)
          const runningAdr = runningRev / runningRooms

          return (
            <div
              key={booking.guest}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all duration-500',
                isVisible
                  ? 'opacity-100 translate-y-0 bg-slate-50/80'
                  : 'opacity-0 translate-y-2 bg-transparent pointer-events-none h-0 py-0 overflow-hidden'
              )}
            >
              {/* Number badge */}
              <span
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white',
                  booking.day >= -7 ? 'bg-emerald-500' : 'bg-primary'
                )}
              >
                {idx + 1}
              </span>

              {/* Guest + details */}
              <div className="flex-1 min-w-0">
                <span className="font-medium text-foreground">{booking.guest}</span>
                <span className="text-muted-foreground"> — {booking.nights} {booking.nights === 1 ? 'night' : 'nights'}, </span>
                <span className="tabular-nums font-semibold text-foreground">£{booking.revenue}</span>
              </div>

              {/* Day */}
              <span className="text-xs tabular-nums text-muted-foreground whitespace-nowrap">
                Day {booking.day}
              </span>

              {/* Running ADR */}
              <span className="text-xs tabular-nums font-medium text-primary whitespace-nowrap">
                ADR £{runningAdr.toFixed(2)}
              </span>
            </div>
          )
        })}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2">
        {!isPlaying ? (
          <Button
            variant="default"
            size="sm"
            onClick={handlePlay}
            className="rounded-lg"
          >
            <Play className="mr-1.5 h-3.5 w-3.5" />
            {isComplete ? 'Replay' : hasStarted ? 'Resume' : 'Play'}
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={handlePause}
            className="rounded-lg"
          >
            <Pause className="mr-1.5 h-3.5 w-3.5" />
            Pause
          </Button>
        )}

        {hasStarted && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="rounded-lg text-muted-foreground"
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Reset
          </Button>
        )}

        {!hasStarted && (
          <span className="text-xs text-muted-foreground">
            Watch bookings arrive one by one over 30 days
          </span>
        )}
      </div>
    </div>
  )
}
